import { useTranslation } from 'react-i18next';
import type { CustomFieldType, FieldWidth } from '@platform/core/types';
import { localized } from '@platform/core/lib/localized';
import { widthOf } from '@platform/core/lib/fieldTypes';
import { fieldEdits } from './fieldEdits';
import type { IFieldRowProps } from './IFieldRowProps';
import { optionsFromText } from './optionsFromText';
import { localText } from './patchText';
import { withCustomField, withFieldOverride } from './patchFields';
import { PiiFieldToggle } from './PiiFieldToggle';
import { TextCommit } from './TextCommit';

/** Типи власного поля (ADR-023 customFields): тип змінюється лише в них. */
const CUSTOM_TYPES = ['text', 'textarea', 'number', 'select', 'date', 'user', 'checkbox'];

/**
 * Рядок «Полів» конструктора (прототип v4 bFields): підпис (зберігається мовою інтерфейсу), ключ, тип (лише у власних полів), ширина у формі
 * («Авто · M» чи S/M/L), варіанти поля вибору через кому, «Обов’язкове», «У списку» і × — вилучити власне чи приховати спроєктоване поле.
 * Під власним полем (крім типу «Користувач» — учасник простору, не персональні дані) — рядок «Персональні дані» (рішення 2 етапу 8);
 * зміна типу на «Користувач» знімає прапорець.
 */
export function FieldRow({ rt, row, strings, canEdit, onEdit }: IFieldRowProps) {
  const { t } = useTranslation();
  const { field, custom, override, system, lockedRequired } = row;
  const lang = strings.lang;
  const label = custom ? localized(custom.label, lang) : override?.label?.[lang] ?? strings.field(rt.key, field.key, field.label);
  const plain = strings.field(rt.key, field.key, row.published?.label ?? null);
  const options = (custom?.options ?? override?.options ?? field.options ?? []).map((o) => ({ value: o.value, label: o.label?.[lang] ?? strings.option(rt.key, field.key, o.value, o.label ?? null) }));
  const select = field.type === 'select' && field.key !== 'status';
  const width = custom ? custom.width ?? null : override && 'width' in override ? override.width ?? null : field.width ?? null;
  const required = custom ? !!custom.required : override?.required ?? !!field.required;
  const inList = custom ? !!custom.inList : override?.inList ?? !!field.inList;
  const type = custom ? custom.type : field.type;
  const typeWhy = custom ? undefined : t(system ? 'rtBuilder.systemType' : 'rtBuilder.designedType');
  const selectOptions = () => (custom?.options?.length ? custom.options : [{ value: t('rtBuilder.optionA') }, { value: t('rtBuilder.optionB') }]);
  const setLabel = (value: string) => onEdit((p) => (custom
    ? withCustomField(p, field.key, { label: localText(lang, value, '') ?? custom.label })
    : withFieldOverride(p, field.key, { label: localText(lang, value, plain) })));
  const pii = !!custom && custom.type !== 'user';
  return (
    <>
      <div className={pii ? 'rb-frow rb-frow--sub' : 'rb-frow'}>
        <div className="rb-frow__cell"><TextCommit className="rb-frow__label" value={label} label={t('rtBuilder.cols.label')} disabled={!canEdit} onCommit={setLabel} /></div>
        <div className="rb-frow__key" title={field.key}>{field.key}</div>
        <div className="rb-frow__cell">
          <select className="rb-select rb-select--sm" value={type} disabled={!canEdit || !custom} title={typeWhy} aria-label={t('rtBuilder.cols.type')}
            onChange={(e) => onEdit(fieldEdits.type(row, e.target.value as CustomFieldType, selectOptions))}>
            {(custom ? CUSTOM_TYPES : [type]).map((ty) => <option key={ty} value={ty}>{t(`rtBuilder.types.${ty}`, { defaultValue: ty })}</option>)}
          </select>
        </div>
        <div className="rb-frow__cell">
          <select className="rb-select rb-select--sm" value={width ?? ''} disabled={!canEdit} aria-label={t('rtBuilder.widthLabel')} title={t('rtBuilder.widthTip')}
            onChange={(e) => onEdit(fieldEdits.width(row, (e.target.value || null) as FieldWidth | null))}>
            <option value="">{t('rtBuilder.widths.auto', { width: widthOf({ ...field, width: null }) })}</option>
            {(['S', 'M', 'L'] as const).map((w) => <option key={w} value={w}>{t(`rtBuilder.widths.${w}`)}</option>)}
          </select>
        </div>
        <div className="rb-frow__cell">
          {field.key === 'status'
            ? <input className="rb-input rb-input--sm" value={rt.statuses.list.map((st) => strings.status(rt.key, st.value, st.label ?? null)).join(', ')} disabled readOnly
              title={t('rtBuilder.statusOptions')} aria-label={t('rtBuilder.commaSeparated')} />
            : select
            ? <TextCommit className="rb-input rb-input--sm" value={options.map((o) => o.label).join(', ')} label={t('rtBuilder.commaSeparated')} placeholder={t('rtBuilder.commaSeparated')}
              disabled={!canEdit} onCommit={(text) => onEdit(fieldEdits.options(row, optionsFromText(text, options, lang)))} />
            : <span className="rb-dash">—</span>}
        </div>
        <div className="rb-frow__check"><input type="checkbox" className="rb-check" checked={required} disabled={!canEdit || (lockedRequired && required)}
          title={lockedRequired ? t('rtBuilder.lockedRequired') : undefined} aria-label={`${t('rtBuilder.cols.required')} · ${label}`} onChange={() => onEdit(fieldEdits.required(row, !required))} /></div>
        <div className="rb-frow__check"><input type="checkbox" className="rb-check" checked={inList} disabled={!canEdit} aria-label={`${t('rtBuilder.cols.inList')} · ${label}`}
          onChange={() => onEdit(fieldEdits.inList(row, !inList))} /></div>
        <div>{!system && !lockedRequired && canEdit && (
          <button type="button" className="rb-x" title={t(custom ? 'rtBuilder.removeField' : 'rtBuilder.hideField')} aria-label={`${t(custom ? 'rtBuilder.removeField' : 'rtBuilder.hideField')} · ${label}`}
            onClick={() => onEdit(fieldEdits.remove(row))}>×</button>
        )}</div>
      </div>
      {pii && <PiiFieldToggle label={label} checked={!!custom?.pii} canEdit={canEdit} onChange={(on) => onEdit(fieldEdits.pii(row, on))} />}
    </>
  );
}
